import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Loader2, RotateCcw, Siren as RescueIcon } from 'lucide-react';

/**
 * Hold length before the alert fires. One constant, in TypeScript, referenced by
 * the animation loop, the label and the aria-label.
 */
export const HOLD_DURATION_MS = 2000;

/** How long the "released too early" explanation stays on screen. */
const EARLY_RELEASE_HINT_MS = 3000;

// Ring geometry in the SVG's own 100x100 user space, so the ring scales with the
// button without recomputing anything.
const RING_RADIUS = 46;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/** A hold shorter than this is a tap, not an attempt, so it gets no scolding hint. */
const TAP_PROGRESS_THRESHOLD = 0.05;

export interface PanicButtonProps {
  /** Called once, the moment the hold completes. */
  onFire: () => void;
  onChargeStart?: () => void;
  onChargeCancel?: () => void;
  /** True while the request is in flight. */
  busy?: boolean;
  /**
   * Non-null blocks the hold. The text is rendered under the button, because a
   * greyed out panic button with no explanation is a support call at 3am.
   */
  blockedReason?: string | null;
}

/** The same number in words for the copy, so no string says "two seconds" on its own. */
export const HOLD_SECONDS = Math.round(HOLD_DURATION_MS / 1000);

export function PanicButton({
  onFire,
  onChargeStart,
  onChargeCancel,
  busy = false,
  blockedReason = null,
}: PanicButtonProps) {
  const disabled = busy || blockedReason !== null;

  const [progress, setProgress] = useState(0);
  const [holding, setHolding] = useState(false);
  const [releasedEarly, setReleasedEarly] = useState(false);

  const rafRef = useRef<number | null>(null);
  const startedAtRef = useRef(0);
  const hintTimerRef = useRef<number | null>(null);
  // The rAF callback and the pointer handlers both need to know, synchronously,
  // whether a hold is live. React state is one render behind for that.
  const holdingRef = useRef(false);
  const progressRef = useRef(0);

  // Kept in refs so the animation loop below can stay identity stable: the loop
  // that is already running must call the current callbacks, not the ones that
  // existed when the finger went down.
  const onFireRef = useRef(onFire);
  const onChargeCancelRef = useRef(onChargeCancel);
  useEffect(() => {
    onFireRef.current = onFire;
    onChargeCancelRef.current = onChargeCancel;
  }, [onFire, onChargeCancel]);

  const stopLoop = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const setProgressValue = useCallback((value: number) => {
    progressRef.current = value;
    setProgress(value);
  }, []);

  /**
   * Progress comes from real elapsed time on every frame rather than from a CSS
   * animation, because a cancel has to know exactly how far the charge got. A CSS
   * animation would look identical and tell us nothing at release time.
   */
  const tick = useCallback<(now: number) => void>(
    (now) => {
      // rAF timestamps share performance.now()'s time origin, so this subtraction
      // is valid.
      const next = Math.min(1, (now - startedAtRef.current) / HOLD_DURATION_MS);
      setProgressValue(next);

      if (next >= 1) {
        rafRef.current = null;
        holdingRef.current = false;
        setHolding(false);
        onFireRef.current();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    },
    [setProgressValue]
  );

  const startHold = useCallback(() => {
    if (disabled || holdingRef.current) return;

    if (hintTimerRef.current !== null) {
      window.clearTimeout(hintTimerRef.current);
      hintTimerRef.current = null;
    }
    setReleasedEarly(false);
    holdingRef.current = true;
    setHolding(true);
    startedAtRef.current = performance.now();
    setProgressValue(0);
    onChargeStart?.();
    rafRef.current = requestAnimationFrame(tick);
  }, [disabled, onChargeStart, setProgressValue, tick]);

  const cancelHold = useCallback(() => {
    if (!holdingRef.current) return;

    holdingRef.current = false;
    stopLoop();
    setHolding(false);

    if (progressRef.current > TAP_PROGRESS_THRESHOLD) {
      setReleasedEarly(true);
      hintTimerRef.current = window.setTimeout(() => {
        hintTimerRef.current = null;
        setReleasedEarly(false);
      }, EARLY_RELEASE_HINT_MS);
    }

    setProgressValue(0);
    onChargeCancelRef.current?.();
  }, [setProgressValue, stopLoop]);

  useEffect(
    () => () => {
      stopLoop();
      if (hintTimerRef.current !== null) window.clearTimeout(hintTimerRef.current);
    },
    [stopLoop]
  );

  // A completed hold leaves the ring full, and it stays full while the request is
  // in flight and through whatever state follows. It empties again only when the
  // button can actually fire, so a full ring always means "already sent".
  useEffect(() => {
    if (!disabled && !holdingRef.current) setProgressValue(0);
  }, [disabled, setProgressValue]);

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    // Space scrolls the page and Enter activates the button on keydown. Both would
    // fight the hold gesture.
    event.preventDefault();
    // A held key autorepeats. The loop is already running, ignore the repeats.
    if (event.repeat) return;
    startHold();
  };

  const handleKeyUp = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    event.preventDefault();
    // No-op when the hold already completed, so a keyup after firing is harmless.
    cancelHold();
  };

  const percent = Math.round(progress * 100);

  let label: string;
  let sublabel: string;
  if (busy) {
    label = 'Llamando';
    sublabel = 'enviando';
  } else if (holding) {
    label = 'Seguí apretando';
    sublabel = `${percent}%`;
  } else if (blockedReason !== null) {
    label = 'Llamar a guardia';
    sublabel = 'no disponible';
  } else {
    label = 'Llamar a guardia';
    sublabel = `mantené ${HOLD_SECONDS}s`;
  }

  const liveMessage = holding
    ? `Cargando, ${percent} por ciento. Seguí apretando.`
    : releasedEarly
      ? 'Se soltó antes de tiempo. No se envió nada.'
      : '';

  return (
    <div className="flex w-full flex-col items-center gap-4">
      <div className="relative size-[15rem] sm:size-[17rem]">

        {/* Charge glow, intensity driven by the same rAF progress as the ring, so
            the button visibly gathers energy instead of just filling an outline.
            Inline style because the value is continuous.

            A radial gradient, NOT a solid disc with `filter: blur()`. The blurred
            version had an opaque core and a comparatively sudden shoulder, so its
            edge read as a hard boundary, a box cropping the glow, rather than a
            falloff. A gradient genuinely reaches zero alpha, and it also avoids
            depending on how a browser sizes a filter region.

            Negative inset so the glow extends past the button. Nothing in the
            ancestor chain clips (verified: only the button itself sets
            overflow-hidden, and this is its sibling), so it has room to fade out. */}
        {holding && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-[-14%] rounded-full motion-safe:transition-opacity"
            style={{
              background: `radial-gradient(circle closest-side, hsl(var(--primary) / ${(0.5 + progress * 0.45).toFixed(3)}) 0%, hsl(var(--primary) / ${(0.28 + progress * 0.3).toFixed(3)}) 46%, hsl(var(--primary) / 0) 78%)`,
              opacity: 0.35 + progress * 0.65,
            }}
          />
        )}

        <svg viewBox="0 0 100 100" className="absolute inset-0 size-full -rotate-90" aria-hidden="true">
          <circle
            cx="50"
            cy="50"
            r={RING_RADIUS}
            fill="none"
            strokeWidth="4"
            className={disabled ? 'stroke-border' : 'stroke-primary/20'}
          />
          <circle
            cx="50"
            cy="50"
            r={RING_RADIUS}
            fill="none"
            strokeWidth="4"
            strokeLinecap="round"
            className="stroke-primary"
            style={{
              strokeDasharray: RING_CIRCUMFERENCE,
              strokeDashoffset: RING_CIRCUMFERENCE * (1 - progress),
              // No drop-shadow here, and that is the fix for the "glow is cropped
              // into a box" bug rather than a style preference.
              //
              // An SVG clips its own painting twice: the <svg> viewport is
              // overflow:hidden by default, and a filter region defaults to the
              // element bounding box plus only 10%. A drop-shadow on this circle
              // therefore got sliced off at straight edges, which is exactly what a
              // hard rectangle around the glow looks like. The glow that reads as
              // "about to fire" is the DOM radial gradient above, which is not
              // inside the SVG and so has nothing clipping it.
            }}
          />
        </svg>

        <button
          type="button"
          disabled={disabled}
          aria-label={`Llamar al ingeniero de guardia. Mantené presionado ${HOLD_SECONDS} segundos para enviar la alerta.`}
          aria-describedby="panic-button-help"
          onPointerDown={(event) => {
            // Secondary buttons and secondary pointers must not start a page.
            if (!event.isPrimary || event.button !== 0) return;
            startHold();
          }}
          onPointerUp={cancelHold}
          onPointerCancel={cancelHold}
          onPointerLeave={cancelHold}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onBlur={cancelHold}
          // A 2 second press is a long press: without this, mobile shows the
          // callout menu and desktop shows the context menu mid charge.
          onContextMenu={(event) => event.preventDefault()}
          className={[
            'absolute inset-[9%] flex select-none flex-col items-center justify-center gap-1.5 overflow-hidden rounded-full',
            'text-center transition-[background-color,box-shadow,transform] duration-150 ease-out',
            'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/40 focus-visible:ring-offset-4',
            'focus-visible:ring-offset-background',
            disabled
              ? 'cursor-not-allowed bg-muted text-muted-foreground shadow-none'
              : 'bg-primary text-primary-foreground shadow-xl shadow-primary/25 hover:bg-primary/95',
            // Held: snap to the bright Restolia red. 100ms, so it lands almost the
            // instant a thumb touches down. A slow fade here would feel like lag on
            // the one control where responsiveness is the whole point.
            holding
              ? 'scale-[0.96] !bg-press text-white shadow-2xl shadow-press/40 duration-100'
              : '',
            !disabled && !holding ? 'motion-safe:hover:scale-[1.02] active:scale-[0.98]' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          // Without this a touch hold pans the page and the browser steals the
          // gesture, cancelling the charge.
          style={{ touchAction: 'none' }}
        >
          {/* Pulse, INSIDE the button.
              
              It lives as a child of the button rather than a sibling, so the
              button's own overflow-hidden clips it to the circle. An earlier version
              sat outside and expanded past the edge, which read as a shadow around
              the button instead of something happening within it.
              
              A radial gradient, not a tinted disc: a flat disc scaled up has a hard
              edge, so the pulse arrived as a step. A gradient fades to zero alpha, so
              it reads as light moving through the face.
              
              White rather than a brand colour, because it has to sit on burgundy when
              idle and on the bright red when held, and a light tint works on both.
              
              Two of them on staggered delays, so the pulse is continuous rather than
              a repeating blink. Decorative only: motion-safe removes them, and the
              ring is what carries the progress. */}
          {!disabled &&
            [0, 1].map((i) => (
              <span
                key={i}
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 hidden rounded-full motion-safe:block motion-safe:animate-pulse-wave"
                style={{
                  background:
                    'radial-gradient(circle closest-side, hsl(0 0% 100% / 0.30) 0%, hsl(0 0% 100% / 0.14) 45%, hsl(0 0% 100% / 0) 72%)',
                  // Idle is slow and calm. Held speeds up only modestly, 1.8s down
                  // to 1.2s: the colour change already carries the urgency, and the
                  // first attempt at 0.95s down to 0.45s read as frantic.
                  animationDuration: holding ? `${1.8 - progress * 0.6}s` : '2.6s',
                  animationDelay: holding
                    ? `${i * (1.8 - progress * 0.6) * 0.5}s`
                    : `${i * 1.3}s`,
                }}
              />
            ))}

          {/* Sheen, only while the request is in flight. Purely decorative, and
              clipped by the button's overflow-hidden, so it sweeps across the face
              rather than the whole layout. */}
          {busy && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 -inset-x-1/2 hidden bg-gradient-to-r from-transparent via-primary-foreground/25 to-transparent motion-safe:block motion-safe:animate-sheen"
            />
          )}
          {busy ? (
            <Loader2 className="size-9 animate-spin" aria-hidden="true" />
          ) : (
            <RescueIcon className={holding ? 'size-9 motion-safe:animate-breathe' : 'size-9'} aria-hidden="true" />
          )}
          <span className="text-xl font-extrabold tracking-tight sm:text-2xl">{label}</span>
          <span className="text-sm font-semibold uppercase tracking-wider opacity-80">{sublabel}</span>
        </button>
      </div>

      {/* State transitions are announced here. The status panel announces outcomes,
          this one announces the gesture. */}
      <p className="sr-only" role="status" aria-live="polite">
        {liveMessage}
      </p>

      <div id="panic-button-help" className="min-h-10 text-center">
        {blockedReason !== null ? (
          <p className="text-sm font-medium text-foreground">{blockedReason}</p>
        ) : releasedEarly ? (
          <p className="flex items-center justify-center gap-1.5 text-sm font-medium text-warning-fg">
            <RotateCcw className="size-4 shrink-0" aria-hidden="true" />
            Se soltó antes de tiempo, no se envió nada. Mantené presionado los {HOLD_SECONDS} segundos
            completos.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            {holding ? 'Soltá ahora para cancelar.' : `Mantené presionado ${HOLD_SECONDS} segundos.`}
          </p>
        )}
      </div>
    </div>
  );
}
